import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SEO } from "../../components/SEO";
import { preloadImages } from "../../utils/imagePreload";
import {
  WorksRoot,
  WorksHeader,
  WorksTitle,
  FilterContainer,
  FilterNavigation,
  FilterItem,
  FilterText,
  FilterUnderline,
  SearchContainer,
  SearchInput,
  SearchIcon,
  WorksGrid,
  WorkCard,
  WorkImage,
  WorkContent,
  WorkTitle,
  WorkTags,
  WorkTag,
  LoadMoreButton,
  NoResultsMessage,
  LockOverlay,
  LockIcon,
} from "./styles";
import { fetchWorks, WorkItem } from "apis/works";

const Works = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 필터 정의
  const filters = [
    { id: "All", label: "All" },
    { id: "BrandStrategy", label: "Brand Strategy" },
    { id: "GrowthMarketing", label: "Growth Marketing" },
    { id: "UGCMarketing", label: "UGC Marketing" },
    { id: "Contents", label: "Contents" }
  ];
  
  // URL에서 초기 필터와 검색어 가져오기
  const getInitialFilterFromURL = () => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filter');
    
    if (filterParam && filters.some(filter => filter.id === filterParam)) {
      return filterParam;
    }
    return "All";
  };
  
  const getInitialSearchFromURL = () => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || "";
  };
  
  // 세션 스토리지에서 visibleCount 가져오기
  const getStoredVisibleCount = () => {
    const storedCount = sessionStorage.getItem('worksVisibleCount');
    return storedCount ? parseInt(storedCount) : 8;
  };
  
  // 초기 상태값 설정
  const [activeFilter, setActiveFilter] = useState(getInitialFilterFromURL());
  const [searchTerm, setSearchTerm] = useState(getInitialSearchFromURL());
  const [visibleCount, setVisibleCount] = useState(getStoredVisibleCount()); // 저장된 값으로 초기화
  const [searchActive, setSearchActive] = useState(getInitialSearchFromURL() !== "");
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const [animationEnabled, setAnimationEnabled] = useState(false); // 애니메이션 활성화 상태
  const filterRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  // 프리로딩 상태를 추적하기 위한 state
  const [preloadedIds, setPreloadedIds] = useState<Set<number>>(new Set());
  
  // URL 쿼리 파라미터 변경 감지 및 상태 업데이트
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filter');
    const searchParam = params.get('search');
    
    // 필터 파라미터가 존재하고 유효한 필터인 경우 설정
    if (filterParam && filters.some(filter => filter.id === filterParam)) {
      setActiveFilter(filterParam);
    } else if (!filterParam) {
      setActiveFilter("All");
    }
    
    // 검색어 파라미터가 존재하는 경우 설정
    if (searchParam) {
      setSearchTerm(searchParam);
      setSearchActive(true);
    } else {
      setSearchTerm("");
      setSearchActive(false);
    }
  }, [location.search]);
  
  // visibleCount 변경 시 세션 스토리지에 저장
  useEffect(() => {
    sessionStorage.setItem('worksVisibleCount', visibleCount.toString());
  }, [visibleCount]);
  
  // 작품 데이터 가져오기
  const [works, setWorks] = useState<WorkItem[]>([]);

  useEffect(() => {
    const loadWorks = async () => {
      try {
        const worksData = await fetchWorks();
        setWorks(worksData as WorkItem[]); // Type assertion to match state type
      } catch (error) {
        console.error('Failed to fetch works:', error);
      }
    };
    loadWorks();
  }, []);
  
  // 작품 정렬 (ID 순)
  const sortedWorks = [...works].sort((a, b) => a.id - b.id);
  
  // 필터링 및 검색 로직 개선
  const filteredWorks = sortedWorks
    .filter(work => 
      activeFilter === "All" || 
      (activeFilter === "BrandStrategy" && work.category.includes("전략")) ||
      (activeFilter === "GrowthMarketing" && work.category.includes("그로스")) ||
      (activeFilter === "UGCMarketing" && work.category.includes("UGC")) ||
      (activeFilter === "Contents" && work.category.includes("콘텐츠"))
    )
    .filter(work => {
      if (searchTerm === "") return true;
      
      const searchTermLower = searchTerm.toLowerCase();
      
      // 제목에서 검색
      if (work.title.toLowerCase().includes(searchTermLower)) return true;
      
      // 태그에서 검색
      if (work.tags.some(tag => tag.toLowerCase().includes(searchTermLower))) return true;
      
      // 클라이언트에서 검색
      if (work.client.toLowerCase().includes(searchTermLower)) return true;
      
      // 브랜드에서 검색
      if (work.brand.toLowerCase().includes(searchTermLower)) return true;
      
      // 설명에서 검색
      if (work.description.toLowerCase().includes(searchTermLower)) return true;
      
      return false;
    });
  
  // 현재 보여줄 작품 목록
  const visibleWorks = filteredWorks.slice(0, visibleCount);
  
  const handleSearchFocus = () => {
    setSearchActive(true);
  };
  
  const handleSearchBlur = () => {
    if (searchTerm === "") {
      setSearchActive(false);
    }
  };
  
  // URL 업데이트 함수
  const updateURL = (filter: string, search: string) => {
    const params = new URLSearchParams();
    
    if (filter !== "All") {
      params.set('filter', filter);
    }
    
    if (search) {
      params.set('search', search);
    }
    
    const queryString = params.toString();
    const newPath = queryString ? `?${queryString}` : '';
    
    // URL 업데이트 (페이지 새로고침 없이)
    navigate(`/works${newPath}`, { replace: true });
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setVisibleCount(8); // 검색 시 보여지는 작품 수 초기화
    
    if (newSearchTerm === "") {
      setSearchActive(false);
    } else {
      setSearchActive(true);
    }
    
    // URL 업데이트
    updateURL(activeFilter, newSearchTerm);
  };
  
  const handleFilterChange = (filterId: string) => {
    // 사용자 클릭 시 애니메이션 활성화
    setAnimationEnabled(true);
    setActiveFilter(filterId);
    setVisibleCount(8); // 필터 변경 시 보여지는 작품 수 초기화
    
    // URL 업데이트
    updateURL(filterId, searchTerm);
  };
  
  const handleLoadMore = () => {
    setVisibleCount(prevCount => prevCount + 8); // 8개씩 추가로 보여주기
  };
  
  // 카드 클릭 핸들러
  const handleCardClick = (work: WorkItem) => {
    // private이 true인 경우 페이지 이동을 막음
    if (!work.private) {
      // 현재 작품 수를 세션 스토리지에 저장하고 이동
      sessionStorage.setItem('worksVisibleCount', visibleCount.toString());
      navigate(`/works/${work.slug}`, {
        state: { workData: work }
      });
      window.scrollTo(0, 0);
    }
  };
  
  // 언더라인 위치 설정
  useEffect(() => {
    const activeIndex = filters.findIndex(filter => filter.id === activeFilter);
    if (activeIndex >= 0 && filterRefs.current[activeIndex]) {
      const activeElement = filterRefs.current[activeIndex];
      if (activeElement) {
        const { offsetLeft, offsetWidth } = activeElement;
        setUnderlineStyle({
          left: offsetLeft,
          width: offsetWidth
        });
      }
    }
  }, [activeFilter, filters]);
  
  // 초기 렌더링 시 언더라인 위치 설정을 위한 추가 useEffect
  useEffect(() => {
    // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 실행
    const timer = setTimeout(() => {
      const activeIndex = filters.findIndex(filter => filter.id === activeFilter);
      if (activeIndex >= 0 && filterRefs.current[activeIndex]) {
        const activeElement = filterRefs.current[activeIndex];
        if (activeElement) {
          const { offsetLeft, offsetWidth } = activeElement;
          setUnderlineStyle({
            left: offsetLeft,
            width: offsetWidth
          });
        }
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, []);
  
  // 이미지 프리로딩 핸들러
  const handleCardHover = async (work: WorkItem) => {
    if (preloadedIds.has(work.id)) return; // 이미 프리로딩된 경우 스킵

    try {
      // 썸네일과 상세 이미지들을 모두 프리로드
      const imagesToPreload = [work.thumbnailUrl];
      await preloadImages(imagesToPreload);
      setPreloadedIds(prev => new Set([...prev, work.id]));
    } catch (error) {
      console.error('Failed to preload images:', error);
    }
  };
  
  // 검색 결과가 없을 때 메시지 표시
  if (filteredWorks.length === 0) {
    // 검색어가 있는 경우와 없는 경우를 구분
    const isSearching = searchTerm.trim() !== "";
    const noResultsMessage = isSearching 
      ? `"${searchTerm}"에 대한 검색 결과가 없습니다.`
      : "해당하는 게시물이 없습니다.";

    return (
      <WorksRoot>
        <SEO 
          title="퍼즐코퍼레이션 | Works - 성과 중심 포트폴리오"
          description="다양한 산업에서 축적한 성공 사례와 프로젝트 레퍼런스를 한눈에 확인하세요. 퍼즐코퍼레이션이 만들어낸 실질적인 성과와 성장 스토리를 통해 비즈니스 혁신의 가능성을 살펴보실 수 있습니다."
          keywords="퍼즐코퍼레이션, PUZL, 포트폴리오, 성공사례, 프로젝트, 그로스마케팅, 퍼포먼스마케팅, IT솔루션"
          ogTitle="퍼즐코퍼레이션 | Works - 성과 중심 포트폴리오"
          ogDescription="다양한 산업에서 축적한 성공 사례와 프로젝트 레퍼런스를 한눈에 확인하세요. 퍼즐코퍼레이션이 만들어낸 실질적인 성과와 성장 스토리를 통해 비즈니스 혁신의 가능성을 살펴보실 수 있습니다."
          ogImage="/images/og-image2.png"
        />
        <WorksHeader>
          <WorksTitle>Works</WorksTitle>
        </WorksHeader>
        
        <FilterContainer>
          <SearchContainer active={searchActive}>
            <SearchInput 
              placeholder="검색어를 입력하세요"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
            />
            <SearchIcon active={searchActive}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15.8037 15.8037L21.0003 21.0003" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </SearchIcon>
          </SearchContainer>
          
          <FilterNavigation>
            {filters.map((filter, index) => (
              <FilterItem 
                key={filter.id}
                active={activeFilter === filter.id}
                onClick={() => handleFilterChange(filter.id)}
                ref={el => filterRefs.current[index] = el}
              >
                <FilterText active={activeFilter === filter.id}>
                  {filter.label}
                </FilterText>
              </FilterItem>
            ))}
            <FilterUnderline 
              style={{ left: `${underlineStyle.left}px`, width: `${underlineStyle.width}px` }}
              className={animationEnabled ? "animated" : ""}
            />
          </FilterNavigation>
        </FilterContainer>
        
        <NoResultsMessage>
          {noResultsMessage}
        </NoResultsMessage>
      </WorksRoot>
    );
  }
  
  return (
    <WorksRoot>
      <SEO 
        title="퍼즐코퍼레이션 | Works - 성과 중심 포트폴리오"
        description="다양한 산업에서 축적한 성공 사례와 프로젝트 레퍼런스를 한눈에 확인하세요. 퍼즐코퍼레이션이 만들어낸 실질적인 성과와 성장 스토리를 통해 비즈니스 혁신의 가능성을 살펴보실 수 있습니다."
        keywords="퍼즐코퍼레이션, PUZL, 포트폴리오, 성공사례, 프로젝트, 그로스마케팅, 퍼포먼스마케팅, IT솔루션"
        ogTitle="퍼즐코퍼레이션 | Works - 성과 중심 포트폴리오"
        ogDescription="다양한 산업에서 축적한 성공 사례와 프로젝트 레퍼런스를 한눈에 확인하세요. 퍼즐코퍼레이션이 만들어낸 실질적인 성과와 성장 스토리를 통해 비즈니스 혁신의 가능성을 살펴보실 수 있습니다."
        ogImage="/images/og-image2.png"
      />
      <WorksHeader>
        <WorksTitle>Works</WorksTitle>
      </WorksHeader>
      
      <FilterContainer>
        <SearchContainer active={searchActive}>
          <SearchInput 
            placeholder="검색어를 입력하세요"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
          />
          <SearchIcon active={searchActive}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15.8037 15.8037L21.0003 21.0003" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SearchIcon>
        </SearchContainer>
        
        <FilterNavigation>
          {filters.map((filter, index) => (
            <FilterItem 
              key={filter.id}
              active={activeFilter === filter.id}
              onClick={() => handleFilterChange(filter.id)}
              ref={el => filterRefs.current[index] = el}
            >
              <FilterText active={activeFilter === filter.id}>
                {filter.label}
              </FilterText>
            </FilterItem>
          ))}
          <FilterUnderline 
            style={{ left: `${underlineStyle.left}px`, width: `${underlineStyle.width}px` }}
            className={animationEnabled ? "animated" : ""}
          />
        </FilterNavigation>
      </FilterContainer>
      
      <WorksGrid>
        {visibleWorks.map(work => (
          <WorkCard 
            key={work.id} 
            onClick={() => handleCardClick(work)}
            onMouseEnter={() => handleCardHover(work)}
            isPrivate={work.private || false}
          >
            <WorkImage>
              <img src={work.thumbnailUrl} alt={work.title} />
              {work.private && (
                <LockOverlay>
                  <LockIcon />
                </LockOverlay>
              )}
            </WorkImage>
            <WorkContent>
              <WorkTitle>{work.title}</WorkTitle>
              <WorkTags>
                {work.workScope && <WorkTag>#{work.workScope}</WorkTag>}
                {work.industry && <WorkTag>#{work.industry}</WorkTag>}
              </WorkTags>
            </WorkContent>
          </WorkCard>
        ))}
      </WorksGrid>
      
      {filteredWorks.length > visibleCount && (
        <LoadMoreButton 
          onClick={handleLoadMore}
          hasMore={true}
        />
      )}
    </WorksRoot>
  );
};

export default Works;
